<main class="blog-page">
  <app-normal-navbar></app-normal-navbar>

  <section class="blog-content">
    <!-- ── Hero Section ─────────────────────────────────────────── -->
    <header class="editorial-hero">
      <h1 class="hero-title">Long-form cinema writing with an editorial pulse.</h1>
      <p class="hero-text">
        Reviews, retrospectives, scene breakdowns, and behind-the-screen notes curated in a magazine layout.
      </p>
    </header>

    <!-- ── Toolbar / Controls ───────────────────────────────────── -->
    <div class="toolbar-panel">
      <div class="toolbar-search">
        <div class="search-input-wrap">
          <svg class="search-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
            stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="11" cy="11" r="8"></circle>
            <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
          </svg>
          <input type="text" [(ngModel)]="filters.search" (keyup.enter)="applyFilters()"
            placeholder="Search essays, films, directors, topics..." aria-label="Search articles" />
          <button *ngIf="filters.search" type="button" class="clear-search-btn"
            (click)="filters.search = ''; applyFilters()" aria-label="Clear search">×</button>
        </div>
      </div>

      <div class="toolbar-filters">
        <div class="select-wrap">
          <select [(ngModel)]="filters.sort_by" (change)="applyFilters()" aria-label="Sort articles">
            <option *ngFor="let option of sortOptions" [value]="option.value">{{ option.label }}</option>
          </select>
          <svg class="select-arrow" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
            stroke-width="2">
            <polyline points="6 9 12 15 18 9"></polyline>
          </svg>
        </div>

        <button type="button" class="filter-chip" [class.active]="filters.featured === 'true'"
          (click)="filters.featured = filters.featured === 'true' ? '' : 'true'; applyFilters()">
          <span class="chip-dot"></span>
          <span>Featured Only</span>
        </button>

        <button type="button" class="btn-search-action" (click)="applyFilters()">
          Search
        </button>

        <div class="view-toggle" aria-label="Layout view switcher">
          <button type="button" class="view-btn" [class.active]="viewMode === 'grid'" (click)="setViewMode('grid')"
            title="Grid view" aria-label="Grid view">
            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <rect x="3" y="3" width="7" height="7"></rect>
              <rect x="14" y="3" width="7" height="7"></rect>
              <rect x="14" y="14" width="7" height="7"></rect>
              <rect x="3" y="14" width="7" height="7"></rect>
            </svg>
          </button>
          <button type="button" class="view-btn" [class.active]="viewMode === 'list'" (click)="setViewMode('list')"
            title="List view" aria-label="List view">
            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <line x1="8" y1="6" x2="21" y2="6"></line>
              <line x1="8" y1="12" x2="21" y2="12"></line>
              <line x1="8" y1="18" x2="21" y2="18"></line>
              <line x1="3" y1="6" x2="3.01" y2="6"></line>
              <line x1="3" y1="12" x2="3.01" y2="12"></line>
              <line x1="3" y1="18" x2="3.01" y2="18"></line>
            </svg>
          </button>
        </div>
      </div>
    </div>

    <!-- ── Loading / Error ──────────────────────────────────────── -->
    <div class="state-container" *ngIf="loading">
      <div class="loader-ring"></div>
      <p class="state-text">Curating cinema stories...</p>
    </div>
    <div class="state-container error" *ngIf="errorMessage && !loading">
      <p class="state-text">{{ errorMessage }}</p>
      <button type="button" class="btn-outline" (click)="applyFilters()">Try Again</button>
    </div>

    <!-- ── Featured Stories Spotlight ───────────────────────────── -->
    <section class="feature-spotlight"
      *ngIf="!loading && !filters.search && !filters.tag && page === 1 && featuredBlogs.length">
      <!-- Main Lead Article -->
      <article class="lead-card" (click)="openBlog(featuredBlogs[0])">
        <div class="lead-media-wrap">
          <img [src]="blogImage(featuredBlogs[0])" [alt]="blogTitle(featuredBlogs[0])" (error)="onImageError($event)"
            loading="lazy" />
          <div class="lead-media-overlay">
            <span class="lead-badge">{{ blogCategory(featuredBlogs[0]) }}</span>
            <span class="lead-read-badge">{{ readTime(featuredBlogs[0]) }} min read</span>
          </div>
        </div>
        <div class="lead-info">
          <div class="lead-meta-top">
            <span class="lead-author">{{ authorName(featuredBlogs[0]) }}</span>
            <span class="meta-dot"></span>
            <span class="lead-date">{{ blogPublishedAt(featuredBlogs[0]) }}</span>
          </div>
          <h2 class="lead-title">{{ blogTitle(featuredBlogs[0]) }}</h2>
          <p class="lead-summary">{{ blogSummary(featuredBlogs[0]) }}</p>
          <div class="lead-footer">
            <span class="read-more-link">
              Read Full Story
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
                stroke-linecap="round" stroke-linejoin="round">
                <line x1="5" y1="12" x2="19" y2="12"></line>
                <polyline points="12 5 19 12 12 19"></polyline>
              </svg>
            </span>
            <span class="section-count-tag" *ngIf="sectionCount(featuredBlogs[0])">
              {{ sectionCount(featuredBlogs[0]) }} chapters
            </span>
          </div>
        </div>
      </article>

      <!-- Side Editor's Picks Rail with thumbnails -->
      <aside class="feature-rail" *ngIf="featuredBlogs.length > 1">
        <div class="rail-header">
          <span class="rail-header-title">Editor's Picks</span>
          <span class="rail-header-rule"></span>
        </div>
        <article class="rail-card" *ngFor="let blog of featuredBlogs.slice(1, 4); let i = index; trackBy: trackByBlog"
          (click)="openBlog(blog)">
          <div class="rail-thumb">
            <img [src]="blogImage(blog)" [alt]="blogTitle(blog)" (error)="onImageError($event)" loading="lazy" />
            <span class="rail-order">{{ '0' + (i + 2) }}</span>
          </div>
          <div class="rail-details">
            <span class="rail-category">{{ blogCategory(blog) }}</span>
            <h3 class="rail-title">{{ blogTitle(blog) }}</h3>
            <div class="rail-meta">
              <span>{{ blogPublishedAt(blog) }}</span>
              <span class="meta-dot"></span>
              <span>{{ readTime(blog) }}m read</span>
            </div>
          </div>
        </article>
      </aside>
    </section>

    <!-- ── Stories Archive (Grid / List View) ───────────────────── -->
    <section class="archive-section" *ngIf="!loading">
      <div class="archive-head">
        <div class="archive-title-group">
          <h2 class="archive-heading">
            <span *ngIf="filters.tag">Stories tagged with "{{ filters.tag }}"</span>
            <span *ngIf="filters.search">Search results for "{{ filters.search }}"</span>
            <span *ngIf="filters.featured === 'true' && !filters.search && !filters.tag">Featured Stories</span>
            <span *ngIf="!filters.tag && !filters.search && filters.featured !== 'true'">Latest Cinema Stories</span>
          </h2>
          <span class="archive-count" *ngIf="total">{{ total }} articles</span>
        </div>

        <div class="archive-filters-active" *ngIf="filters.search || filters.tag || filters.featured">
          <span class="active-filter-label">Active:</span>
          <span class="active-pill" *ngIf="filters.search">Search: {{ filters.search }}</span>
          <span class="active-pill" *ngIf="filters.tag">Topic: {{ filters.tag }}</span>
          <span class="active-pill" *ngIf="filters.featured">Featured</span>
          <button type="button" class="reset-filters-link" (click)="clearFilters()">Clear all</button>
        </div>
      </div>

      <!-- GRID VIEW -->
      <div class="story-grid"
        *ngIf="blogs.length && viewMode === 'grid'; else (blogs.length ? listViewTemplate : emptyState)">
        <article class="story-card" *ngFor="let blog of blogs; trackBy: trackByBlog" (click)="openBlog(blog)">
          <div class="card-media">
            <img [src]="blogImage(blog)" [alt]="blogTitle(blog)" (error)="onImageError($event)" loading="lazy" />
            <span class="card-category-badge">{{ blogCategory(blog) }}</span>
            <span class="card-read-chip">{{ readTime(blog) }}m read</span>
          </div>

          <div class="card-body">
            <div class="card-meta">
              <span class="card-author">{{ authorName(blog) }}</span>
              <span class="meta-dot"></span>
              <span class="card-date">{{ blogPublishedAt(blog) }}</span>
            </div>

            <h3 class="card-title">{{ blogTitle(blog) }}</h3>
            <p class="card-excerpt">{{ blogSummary(blog) }}</p>

            <div class="card-footer">
              <div class="card-tags" *ngIf="blogTags(blog).length">
                <span class="tag-pill" *ngFor="let tag of blogTags(blog).slice(0, 2)">#{{ tag }}</span>
              </div>
              <span class="card-arrow" aria-hidden="true">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M5 12h14M12 5l7 7-7 7" />
                </svg>
              </span>
            </div>
          </div>
        </article>
      </div>

      <!-- LIST VIEW -->
      <ng-template #listViewTemplate>
        <div class="story-list">
          <article class="story-row-card" *ngFor="let blog of blogs; trackBy: trackByBlog" (click)="openBlog(blog)">
            <div class="row-media">
              <img [src]="blogImage(blog)" [alt]="blogTitle(blog)" (error)="onImageError($event)" loading="lazy" />
              <span class="row-category-badge">{{ blogCategory(blog) }}</span>
            </div>

            <div class="row-content">
              <div class="row-meta">
                <span class="row-author">{{ authorName(blog) }}</span>
                <span class="meta-dot"></span>
                <span class="row-date">{{ blogPublishedAt(blog) }}</span>
                <span class="meta-dot"></span>
                <span class="row-read">{{ readTime(blog) }} min read</span>
                <span class="section-chip" *ngIf="sectionCount(blog)">{{ sectionCount(blog) }} chapters</span>
              </div>

              <h3 class="row-title">{{ blogTitle(blog) }}</h3>
              <p class="row-excerpt">{{ blogSummary(blog) }}</p>

              <div class="row-footer">
                <div class="row-tags" *ngIf="blogTags(blog).length">
                  <span class="tag-pill" *ngFor="let tag of blogTags(blog).slice(0, 3)">#{{ tag }}</span>
                </div>
                <span class="row-action">
                  Read article
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M5 12h14M12 5l7 7-7 7" />
                  </svg>
                </span>
              </div>
            </div>
          </article>
        </div>
      </ng-template>

      <!-- EMPTY STATE -->
      <ng-template #emptyState>
        <div class="empty-state-card">
          <div class="empty-icon">
            <svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
              <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"></path>
              <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"></path>
            </svg>
          </div>
          <h3>No cinema stories found</h3>
          <p>We couldn't find any articles matching your search criteria or selected topic.</p>
          <button type="button" class="btn-primary" (click)="clearFilters()">
            Reset All Filters
          </button>
        </div>
      </ng-template>
    </section>

    <!-- ── Scroll Pagination Sentinel & States ──────────────────── -->
    <div #scrollSentinel class="scroll-sentinel" aria-hidden="true"></div>

    <!-- Scroll Loading More Spinner -->
    <div class="scroll-loading-more" *ngIf="loadingMore">
      <div class="scroll-spinner-ring"></div>
      <span class="scroll-loading-text">Loading more cinema stories...</span>
    </div>

    <!-- End of Archive Marker -->
    <div class="scroll-end-marker" *ngIf="!loading && !loadingMore && blogs.length > 0 && page >= totalPages">
      <div class="end-marker-rule"></div>
      <span class="end-marker-badge">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
          <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"></polygon>
        </svg>
        You have caught up with all cinema stories
      </span>
      <div class="end-marker-rule"></div>
    </div>
  </section>

  <app-footer></app-footer>
</main>